import { Dropdown, Icon, IconButton, Modal } from "materia"
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEventHandler,
  type ReactNode,
} from "react"
import { useTranslation } from "react-i18next"
import Markdown from "react-markdown"
import remarkBreaks from "remark-breaks"
import remarkGfm from "remark-gfm"
import { modifiers } from "ui-tools"
import { linkify } from "../../../../tools/linkify"
import { Avatar } from "../../../Avatar"
import type { ChatMessage } from "../../AIChat"
import s from "./Chat.module.scss"

export type AssistantOption = {
  id: string
  title: string
  avatar: string
}

export const Chat = ({
  isOpen,
  onClose,
  title,
  tagline,
  userAvatar,
  assistantAvatar,
  questionSuggestions = [],
  onSubmit,
  messages,
  isPendingAnswer,
  assistants,
  onSelectAssistant,
  onReset,
  isResetDisabled = false,
  notice = null,
}: {
  isOpen: boolean
  onClose: () => void
  title: string
  tagline: string | null
  userAvatar: string
  assistantAvatar: string
  questionSuggestions: string[]
  onSubmit: (question: string) => void
  messages: ChatMessage[]
  isPendingAnswer: boolean
  assistants?: AssistantOption[]
  onSelectAssistant?: (id: string) => void
  onReset?: () => void
  isResetDisabled?: boolean
  /** A transient status line above the input, kept out of the transcript. */
  notice?: string | null
}) => {
  const { t } = useTranslation()
  const [question, setQuestion] = useState("")
  const messagesScrollContainerRef = useRef<HTMLDivElement | null>(null)
  const isChatEmpty = messages.length === 0

  // Keep chat scrolled to bottom on incoming or outgoing message
  useEffect(() => {
    if (!messagesScrollContainerRef.current) return

    messagesScrollContainerRef.current.scrollTop =
      messagesScrollContainerRef.current.scrollHeight
  }, [messages])
  if (!isOpen) return null

  const handleSubmitQuestion = () => {
    const trimmedQuestion = question.trim()
    const isNoQuestionPresent = trimmedQuestion.length === 0

    if (isPendingAnswer || isNoQuestionPresent) return

    onSubmit(trimmedQuestion)
    setQuestion("")
  }

  return (
    <Modal
      ariaLabel="AI chat window"
      onClose={onClose}
      isFullScreenMobile={true}
    >
      <div className={s.aiChat}>
        <div className={s.aiChat__topBar}>
          <div className={s.aiChat__header}>
            {assistants && assistants.length > 1 ? (
              <AssistantDropdown
                assistants={assistants}
                currentTitle={title}
                currentAvatar={assistantAvatar}
                onSelect={onSelectAssistant}
              />
            ) : (
              <>
                <Avatar size="small" image={assistantAvatar} alt={title} />
                <span className={s.aiChat__title}>{title}</span>
              </>
            )}

            <IconButton
              as="button"
              variant="ghost"
              size="medium"
              aria-label="Reset chat"
              icon="Refresh"
              onClick={onReset}
              disabled={isChatEmpty || isResetDisabled}
            />
          </div>

          <IconButton
            as="button"
            aria-label="Close"
            className={s.closeButton}
            icon="Close"
            onClick={onClose}
          />
        </div>

        <div ref={messagesScrollContainerRef} className={s.aiChat__main}>
          {isChatEmpty && (
            <ChatIntro
              questionSuggestions={questionSuggestions}
              onClickSuggestion={(question) => {
                if (!isPendingAnswer) onSubmit(question)
              }}
              assistantAvatar={assistantAvatar}
              assistantName={title}
              tagline={tagline}
            />
          )}

          {/* Index keys: the transcript is append-only and never reorders. */}
          {messages.map((message, index) => {
            const isLastMessage = messages.length - 1 === index
            if (message.messageType === "Question") {
              // Get avatars and descriptions from BE
              return (
                <ChatQuestion
                  // biome-ignore lint/suspicious/noArrayIndexKey: append-only transcript
                  key={index}
                  text={message.text}
                  avatar={userAvatar}
                  onPortalLinkClick={onClose}
                />
              )
            } else {
              return (
                <ChatResponse
                  // biome-ignore lint/suspicious/noArrayIndexKey: append-only transcript
                  key={index}
                  text={message.text}
                  isLoading={isLastMessage && isPendingAnswer}
                  avatar={assistantAvatar}
                  isError={message.messageType === "Error"}
                  onPortalLinkClick={onClose}
                />
              )
            }
          })}
        </div>

        <div className={s.aiChat__footer}>
          {notice && (
            <div className={s.aiChat__notice} role="alert">
              {notice}
            </div>
          )}
          <ChatTextInput
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            onSubmit={handleSubmitQuestion}
          />
          <div className={s.aiChat__warning}>
            {t("ai_assistant_chat.WARNING")}
          </div>
        </div>
      </div>
    </Modal>
  )
}

const AssistantDropdown = ({
  assistants,
  currentTitle,
  currentAvatar,
  onSelect,
}: {
  assistants: AssistantOption[]
  currentTitle: string
  currentAvatar: string
  onSelect?: (id: string) => void
}) => {
  const selected =
    assistants.find((assistant) => assistant.title === currentTitle) ??
    assistants[0]

  return (
    <Dropdown
      value={selected}
      onChange={(assistant) => assistant && onSelect?.(assistant.id)}
    >
      <Dropdown.CustomButton>
        <div className={s.assistantDropdownButton}>
          <Avatar size="small" image={currentAvatar} alt={currentTitle} />
          <div className={s.assistantDropdownButton__title}>{currentTitle}</div>
          <Dropdown.ChevronIcon />
        </div>
      </Dropdown.CustomButton>
      <Dropdown.Options>
        {assistants.map((assistant) => (
          <Dropdown.Option key={assistant.id} value={assistant}>
            <div className={s.assistantDropdownOption}>
              <Avatar
                size="small"
                image={assistant.avatar}
                alt={assistant.title}
              />
              {assistant.title}
            </div>
          </Dropdown.Option>
        ))}
      </Dropdown.Options>
    </Dropdown>
  )
}

const ChatIntro = ({
  questionSuggestions,
  assistantAvatar,
  assistantName,
  tagline,
  onClickSuggestion,
}: {
  questionSuggestions: string[]
  assistantAvatar: string
  assistantName: string
  tagline: string | null
  onClickSuggestion: (question: string) => void
}) => {
  const { t } = useTranslation()
  // FAST returns "null" for every empty question suggestion
  const questions = questionSuggestions.filter(Boolean)

  return (
    <div className={s.chatIntro}>
      <div className={s.chatIntro__header}>
        <Avatar
          className={s.chatIntro__avatar}
          size="extraLarge"
          image={assistantAvatar}
          alt={assistantName}
        />

        <div className={s.chatIntro__assistantName}>
          {assistantName} <span className={s.chatIntro__status}>●</span>
        </div>

        <span>{tagline || t("ai_assistant_chat.INTRO_MESSAGE")}</span>
      </div>

      <div className={s.questionSuggestions}>
        {questions.map((question) => (
          <button
            type="button"
            onClick={() => onClickSuggestion(question)}
            className={s.questionSuggestions__suggestion}
            key={question}
          >
            {question}
          </button>
        ))}
      </div>
    </div>
  )
}

const ChatTextInput = ({
  value,
  onChange,
  onSubmit,
}: {
  value: string
  onChange: React.ChangeEventHandler<HTMLTextAreaElement>
  onSubmit: () => void
}) => {
  const { t } = useTranslation()
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)

  // Let chat input dynamically increase and decrease in height as the input is filled up
  const adjustTextAreaHeight = () => {
    if (!textAreaRef.current?.style) return

    // Allow text area to shrink in height text content is reduced
    textAreaRef.current.style.height = "auto"
    // Allow text area to grow in height as text content is increased
    textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`
    textAreaRef.current.rows = 1
  }

  useEffect(() => {
    adjustTextAreaHeight()
  }, [value])

  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      event.stopPropagation()
      onSubmit()
    }
  }

  return (
    <div className={s.chatTextInput}>
      <textarea
        ref={textAreaRef}
        className={s.chatTextInput__input}
        placeholder={t("ai_assistant_chat.MESSAGE")}
        value={value}
        onKeyDown={handleKeyDown}
        onInput={adjustTextAreaHeight}
        rows={1}
        onChange={onChange}
      />

      <button
        type="submit"
        className={s.chatTextInput__submitButton}
        onClick={onSubmit}
        aria-label="submit question"
      >
        <Icon
          size="small"
          icon="ArrowChunky"
          className={s.chatTextInput__submitButton__icon}
        />
      </button>
    </div>
  )
}

// A session not remembered lives in this tab only, so a new tab would open on
// the login screen. Portal links stay in the tab, and close the chat so it
// does not cover the page they open.
const isPortalLink = (href: string) => {
  try {
    return new URL(href, window.location.href).origin === window.location.origin
  } catch {
    return false
  }
}

const ChatLink = ({
  href,
  children,
  onPortalLinkClick,
}: {
  href: string
  children: ReactNode
  onPortalLinkClick?: () => void
}) =>
  isPortalLink(href) ? (
    <a href={href} onClick={onPortalLinkClick}>
      {children}
    </a>
  ) : (
    <a href={href} target="_blank" rel="noopener noreferrer nofollow">
      {children}
    </a>
  )

const LinkifiedText = ({
  text,
  onPortalLinkClick,
}: {
  text: string
  onPortalLinkClick?: () => void
}) => (
  <>
    {linkify(text).map((segment, i) =>
      segment.type === "link" ? (
        <ChatLink
          // biome-ignore lint/suspicious/noArrayIndexKey: segments are positional; the text only grows while streaming.
          key={i}
          href={segment.value}
          onPortalLinkClick={onPortalLinkClick}
        >
          {segment.value}
        </ChatLink>
      ) : (
        segment.value
      )
    )}
  </>
)

// remark-breaks keeps single line breaks, as the plain text answers had them.
const MARKDOWN_PLUGINS = [remarkGfm, remarkBreaks]

// An image would load as soon as it renders, so a prompt-injected image URL
// could leak chat data in its query string. Links at least need a click.
const DISALLOWED_MARKDOWN_ELEMENTS = ["img"]

/**
 * Assistant answers are markdown. HTML in it is rendered as text, and unsafe
 * hrefs like javascript: come out empty, so no sanitizer is needed.
 */
const MarkdownText = ({
  text,
  onPortalLinkClick,
}: {
  text: string
  onPortalLinkClick?: () => void
}) => (
  <Markdown
    remarkPlugins={MARKDOWN_PLUGINS}
    disallowedElements={DISALLOWED_MARKDOWN_ELEMENTS}
    components={{
      a: ({ href, children }) =>
        href ? (
          <ChatLink href={href} onPortalLinkClick={onPortalLinkClick}>
            {children}
          </ChatLink>
        ) : (
          children
        ),
      // Wide tables scroll sideways instead of stretching the chat.
      table: ({ children }) => (
        <div className={s.markdown__table}>
          <table>{children}</table>
        </div>
      ),
    }}
  >
    {text}
  </Markdown>
)

export const ChatQuestion = ({
  text,
  avatar,
  onPortalLinkClick,
}: {
  text: string
  avatar: string
  onPortalLinkClick?: () => void
}) => {
  return (
    <div className={s.chatQuestion}>
      <div className={s.chatQuestion__text}>
        <LinkifiedText text={text} onPortalLinkClick={onPortalLinkClick} />
      </div>

      <Avatar size="small" image={avatar} alt={""} />
    </div>
  )
}

export const ChatResponse = ({
  text,
  isLoading = false,
  avatar,
  isError,
  onPortalLinkClick,
}: {
  text: string
  isLoading?: boolean
  avatar: string
  isError: boolean
  onPortalLinkClick?: () => void
}) => {
  return (
    <div className={s.chatResponse} aria-busy={isLoading}>
      <Avatar size="small" image={avatar} alt="" />

      <div className={modifiers(s, "chatResponse__text", { isError })}>
        {isError && (
          <Icon icon="ExclamationMarkCircle" className={s.chatResponse__icon} />
        )}
        {isError ? (
          <span aria-live="polite">{text}</span>
        ) : (
          <div aria-live="polite" className={s.markdown}>
            <MarkdownText text={text} onPortalLinkClick={onPortalLinkClick} />
          </div>
        )}
        {/* TODO Improve loading animation. Just here to indicate loading for now */}
        {isLoading && <div className={s.loader} />}
      </div>
    </div>
  )
}
